import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FolderDto } from '@picflow/types';
import { BehaviorSubject, Observable, Subject, lastValueFrom } from 'rxjs';
import { AppConfigService } from '../providers/config.service';
import { DataLoaderService } from './data-loader.service';
import { StateService } from './state.service';

@Injectable({
  providedIn: 'root',
})
export class FolderService {
  private http = inject(HttpClient);
  private config = inject(AppConfigService);
  private loader = inject(DataLoaderService);
  private state = inject(StateService);
  private _updates: Subject<[string, string, FolderDto]> = new BehaviorSubject<[string, string, FolderDto]>(undefined);
  public readonly onUpdates: Observable<[string, string, FolderDto]> = this._updates.asObservable();
  // Note: it is possible to pass a parent folder id but we don't because we also need to fetch subfolders and subsubfolders
  // Which is less efficient then just doing one requst for all folders
  public async getFolders(parentFolderId?: string): Promise<FolderDto[]> {
    if (!this.state.activeTenant) return [];
    const resp = await this.loader.get(`folders`, () => this.getFoldersInternal());
    if (!parentFolderId) return resp;
    if (parentFolderId === 'none') return resp.filter(f => !f.parent_folder);
    return resp.filter(f => f.parent_folder === parentFolderId);
  }

  public async getFolderSubFolders(folderId: string): Promise<FolderDto[]> {
    const folders = await this.getFolders();
    return this.recursiveFilterFolders(folderId, folders);
  }

  private recursiveFilterFolders(folderId: string, folders: FolderDto[]): FolderDto[] {
    const allFolders = [];
    const validFolders = folders.filter(folder => folder.parent_folder === folderId);
    validFolders.forEach(f => {
      allFolders.push(f);
      allFolders.push(...this.recursiveFilterFolders(f.id, folders));
    });
    return allFolders;
  }

  public async getFolder(id: string): Promise<FolderDto> {
    const folders = await this.loader.getFromCache<FolderDto[]>('folders');
    if (folders?.find(g => g.id === id)) return folders.find(g => g.id === id);
    return this.loader.get(`folders_${id}`, () => this.getFolderInternal(id));
  }

  public async createFolder(title: string, parentFolderId?: string) {
    const params = { title, parent_folder: parentFolderId };
    const folder = <FolderDto>await lastValueFrom(this.http.post(`${this.config.apiEndpoint}/folders`, params));
    this.loader.removeFuzzy('folders');
    this._updates.next(['created', folder.id, folder]);
    return folder;
  }

  private async findTopFolder(folder: FolderDto) {
    if (!folder.parent_folder) return folder;
    const folders = await this.getFolders();
    if (folder.id === folder.parent_folder) throw new Error('Folder cannot be its own parent');
    return folders.find(f => f.id === folder.parent_folder);
  }

  public async updateFolder(folder: FolderDto) {
    await this.findTopFolder(folder); // making sure we don't have a circular reference
    if (folder.id === folder.parent_folder) {
      throw new Error('Folder cannot be its own parent');
    }
    await lastValueFrom(this.http.patch(`${this.config.apiEndpoint}/folders/${folder.id}`, folder));
    this.loader.removeFuzzy('folders');
    this._updates.next(['created', folder.id, folder]);
  }

  public async deleteFolder(folderId: string) {
    await lastValueFrom(this.http.delete(`${this.config.apiEndpoint}/folders/${folderId}`));
    this._updates.next(['deleted', folderId, undefined]);
    this.loader.removeFuzzy('folders');
  }

  private async getFolderInternal(id: string) {
    return <FolderDto>await lastValueFrom(this.http.get(this.config.apiEndpoint + '/folders/' + id));
  }

  private async getFoldersInternal(page = 0, results = []): Promise<FolderDto[]> {
    const limit = 100; // need to figure out why this is needed.. ideally we should just be able to remove the limit but right now we only get up to 20 results
    const url = `${this.config.apiEndpoint}/folders?limit=${limit}&page=${page}`;
    const resp = <{ data: FolderDto[] }>await lastValueFrom(this.http.get(url));
    results = results.concat(resp.data);
    if (resp.data.length === limit) return this.getFoldersInternal(page + 1, results);
    return results;
  }
}
